
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import AssetTable from "./AssetTable";

const BASE_URL = process.env.REACT_APP_API_URL;
const ENDPOINT = "/dq-hollistic/asset-details";

const PAGE_SIZE = 80;

const AssetsDetails = ({ filters, setFilters, onFilterOptionsChange, onActiveSearchParamsChange }) => {
  const { parentDomain, domain, subDomain, product, startDate, endDate } = filters;

  const [accumulatedRows, setAccumulatedRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [assetCount, setAssetCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pageRef = useRef(1);

  const [sortBy, setsortBy] = useState(null);
  const [sortTableBy, setsortTableBy] = useState(null);
  const [activeSearchParams, setActiveSearchParams] = useState({});
  const [columnSearch, setColumnSearch] = useState({
    domain_name: "",
    sub_domain_name: "",
    product_name: "",
    data_asset_name: "",
    execution_date: "",
    frequency: "",
    total_dq_score: "",
    region: "",
    status: "",
  });

  const fetchData = async (append = false) => {
    if (!append) setLoading(true);
    setError(null);

    try {
  const res = await axios.get(`${BASE_URL}${ENDPOINT}`, {
            params: {
          parentDomain: Array.isArray(parentDomain) && parentDomain.length > 0 ? parentDomain.join(",") : "",
          domain: Array.isArray(domain) && domain.length > 0 ? domain.join(",") : "",
          subDomain: Array.isArray(subDomain) && subDomain.length > 0 ? subDomain.join(",") : "",
          product: Array.isArray(product) && product.length > 0 ? product.join(",") : "",
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          page: pageRef.current,
          pageSize: PAGE_SIZE,
          sortBy,
          sortOrder: sortTableBy,
          ...activeSearchParams,
        },
      });

      const newRows = res.data.data || [];
      const pag = res.data.pagination || {};

      if (append) {
        setAccumulatedRows((prev) => [...prev, ...newRows]);
      } else {
        setAccumulatedRows(newRows);
      }

      setTotalRecords(pag.totalCount || 0);
      setAssetCount(res.data.assetCount || 0);
      setHasMore((pag.page || 1) < (pag.totalPages || 1));

      if (res.data.filters?.parent_domains && typeof onFilterOptionsChange === "function") {
        onFilterOptionsChange(res.data.filters.parent_domains);
      }
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      if (!append) setLoading(false);
    }
  };

  const loadMoreAssets = async () => {
    if (isFetchingMore || !hasMore) return;
    setIsFetchingMore(true);
    try {
      pageRef.current += 1;
      await fetchData(true);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const handleColumnSearch = (key, value) => {
    setColumnSearch((prev) => ({ ...prev, [key]: value }));
  };

  const normalizeDateSearch = (value) =>
    value.replace(/(\s|^)(\d)(\s|,|$)/g, (_, pre, digit, post) => `${pre}0${digit}${post}`);

  const handleSearchSubmit = (key, value) => {
    const normalizedValue = key === "execution_date" ? normalizeDateSearch(value) : value;
    setActiveSearchParams((prev) => ({ ...prev, [key]: normalizedValue }));
  };

  const handleReset = () => {
    setColumnSearch({
      domain_name: "",
      sub_domain_name: "",
      product_name: "",
      data_asset_name: "",
      execution_date: "",
      frequency: "",
      total_dq_score: "",
      region: "",
      status: "",
    });
    setActiveSearchParams({});
  };

  useEffect(() => {
    pageRef.current = 1;
    fetchData(false);
  }, [parentDomain, domain, subDomain, product, sortBy, sortTableBy, activeSearchParams, startDate, endDate]);

  useEffect(() => {
    onActiveSearchParamsChange?.(activeSearchParams);
  }, [activeSearchParams]);

  return (
    <div>
      <div className="pl-4 pr-4">
        <AssetTable
          tableData={accumulatedRows}
          loading={loading}
          assetCount={assetCount}
          error={error}
          totalRecords={totalRecords}
          loadMore={loadMoreAssets}
          hasMore={hasMore}
          isFetchingMore={isFetchingMore}
          onSearchSubmit={handleSearchSubmit}
          onReset={handleReset}
          onColumnSearchChange={handleColumnSearch}
          columnSearch={columnSearch}
          onSortChange={(column, order) => {
            setsortBy(column);
            setsortTableBy(order);
          }}
        />
      </div>
    </div>
  );
};

export default AssetsDetails;
